import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Sections sourced directly from acumenfs.co.uk
export const SERVICE_SECTIONS = [
    { id: "what-we-do", label: "What We Do" },
    { id: "residential-mortgages", label: "Residential Mortgages" },
    { id: "buy-to-let", label: "Buy-to-Let Mortgages" },
    { id: "residential-bridging", label: "Residential Bridging" },
    { id: "protection-insurance", label: "Protection & Insurance" },
    { id: "our-approach", label: "Our Approach" },
    { id: "about-us", label: "About Us" },
];

const NAV = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services", dropdown: true },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        setMobileServicesOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-navbar"
            className={`sticky top-0 z-50 border-b transition-colors duration-300 ${
                scrolled
                    ? "bg-brand-bg/85 backdrop-blur-xl border-brand-border"
                    : "bg-brand-bg/70 backdrop-blur-md border-transparent"
            }`}
        >
            <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16 flex items-center justify-between h-20">
                <Link to="/" data-testid="nav-logo-link" aria-label="Acumen home">
                    <Logo />
                </Link>

                <nav className="hidden md:flex items-center gap-10">
                    {NAV.map((item) => {
                        if (item.dropdown) {
                            return (
                                <DropdownMenu key={item.to}>
                                    <DropdownMenuTrigger
                                        data-testid="nav-services-trigger"
                                        className="flex items-center gap-1.5 text-sm tracking-wide text-brand-muted hover:text-brand-primary transition-colors outline-none group"
                                    >
                                        <span className="link-underline">{item.label}</span>
                                        <ChevronDown
                                            size={14}
                                            className="transition-transform group-data-[state=open]:rotate-180"
                                        />
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                        align="start"
                                        sideOffset={16}
                                        data-testid="nav-services-menu"
                                        className="min-w-[340px] bg-brand-surface border border-brand-border rounded-none p-2 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.2)]"
                                    >
                                        {SERVICE_SECTIONS.map((s) => (
                                            <DropdownMenuItem
                                                key={s.id}
                                                asChild
                                                className="rounded-none focus:bg-brand-surfaceAlt/60"
                                            >
                                                <Link
                                                    to={`/services#${s.id}`}
                                                    data-testid={`nav-services-item-${s.id}`}
                                                    className="block w-full px-4 py-3 font-serif-editorial text-lg text-brand-primary hover:text-brand-accent transition-colors"
                                                >
                                                    {s.label}
                                                </Link>
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            );
                        }
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                data-testid={`nav-link-${item.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `text-sm tracking-wide link-underline transition-colors ${
                                        isActive
                                            ? "text-brand-primary"
                                            : "text-brand-muted hover:text-brand-primary"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        );
                    })}
                </nav>

                <div className="hidden md:flex items-center gap-4">
                    <Link
                        to="/contact"
                        data-testid="nav-cta-quote"
                        className="bg-white text-brand-primary border-2 border-brand-primary px-6 py-[10px] rounded-full text-sm tracking-wide font-bold hover:bg-brand-primary hover:text-white transition-colors duration-300"
                    >
                        Get in Touch
                    </Link>
                </div>

                <button
                    data-testid="nav-mobile-toggle"
                    className="md:hidden p-2 text-brand-primary"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Toggle navigation"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="nav-mobile-panel"
                    className="md:hidden border-t border-brand-border bg-brand-bg"
                >
                    <div className="px-6 py-6 flex flex-col gap-4">
                        {NAV.map((item) => {
                            if (item.dropdown) {
                                return (
                                    <div key={item.to}>
                                        <button
                                            data-testid="mobile-nav-services-toggle"
                                            onClick={() =>
                                                setMobileServicesOpen((v) => !v)
                                            }
                                            className="w-full flex items-center justify-between text-base text-brand-muted"
                                        >
                                            <span>{item.label}</span>
                                            <ChevronDown
                                                size={16}
                                                className={`transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`}
                                            />
                                        </button>
                                        {mobileServicesOpen && (
                                            <div className="mt-3 pl-4 flex flex-col gap-3 border-l border-brand-border">
                                                {SERVICE_SECTIONS.map((s) => (
                                                    <Link
                                                        key={s.id}
                                                        to={`/services#${s.id}`}
                                                        data-testid={`mobile-nav-services-item-${s.id}`}
                                                        className="text-sm font-serif-editorial text-lg text-brand-primary"
                                                    >
                                                        {s.label}
                                                    </Link>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            }
                            return (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                                    className={({ isActive }) =>
                                        `text-base ${
                                            isActive
                                                ? "text-brand-primary"
                                                : "text-brand-muted"
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            );
                        })}
                        <Link
                            to="/contact"
                            data-testid="mobile-nav-cta"
                            className="mt-2 bg-white text-brand-primary border-2 border-brand-primary px-6 py-3 text-sm text-center rounded-full font-bold"
                        >
                            Get in Touch
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
